'use client';

/**
 * Cloudflare Turnstile wrapper used on every public auth form (login,
 * signup, forgot-password). Closes VAPT finding #3 (CAPTCHA not
 * implemented) and adds the bot-protection layer recommended alongside
 * finding #2 (rate limiting).
 *
 * Why this component vs. dropping <Turnstile> inline on each page:
 *   - One source of truth for site key + appearance/theme defaults.
 *   - Graceful degrade when NEXT_PUBLIC_TURNSTILE_SITE_KEY is unset
 *     (preview deploys, local dev without Turnstile credentials) —
 *     the form stays usable instead of breaking auth entirely.
 *   - One place to reset the widget after a failed submit so users
 *     never see the dreaded "captcha already used" error on retry.
 *
 * Token contract:
 *   - `onToken(token)` fires with a valid token once the user passes
 *     the challenge. The parent must store the token in state and
 *     forward it to the backend on submit.
 *   - `onToken(null)` fires when the token expires (default 300s on
 *     Cloudflare's side) or the widget errors — the parent should
 *     clear its captured token and disable submit.
 *
 * Tied to: backend supabase calls `auth.sign_in_with_password`,
 * `auth.sign_up`, `auth.reset_password_for_email` which receive the
 * token via options={"captcha_token": ...}. Supabase verifies the
 * token against Cloudflare's siteverify endpoint server-side using
 * the secret key configured on the Supabase dashboard.
 */

import { useEffect, useRef } from 'react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';

interface TurnstileWidgetProps {
  /**
   * Called with a token when the challenge is solved, or null when
   * the token expires / errors. The parent should treat null as
   * "captcha not ready" and block submission.
   */
  onToken: (token: string | null) => void;
  /**
   * Optional callback invoked when the widget is rendered (i.e. the
   * site key is configured and the script loaded). Lets the parent
   * decide whether to keep submit disabled until the user solves the
   * challenge vs. allow-through on misconfigured deploys.
   */
  onReady?: () => void;
  /**
   * The challenge action — passed to Cloudflare for analytics + risk
   * scoring per-form. Lets ops separate login vs signup vs reset
   * traffic patterns on the Cloudflare dashboard.
   */
  action?: 'login' | 'signup' | 'forgot-password' | 'reset-password';
  className?: string;
}

/**
 * Imperative ref handle exposed so the parent can reset the widget
 * after a failed login (Cloudflare bans re-using a consumed token).
 */
export interface TurnstileWidgetHandle {
  reset: () => void;
}

export function TurnstileWidget({
  onToken,
  onReady,
  action = 'login',
  className,
}: TurnstileWidgetProps) {
  const ref = useRef<TurnstileInstance | null>(null);
  const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

  // Local-dev / preview-deploy escape hatch: if the env var isn't
  // wired, do not render the widget and signal "ready" with a
  // sentinel token. The backend MUST reject this sentinel in any
  // non-dev environment (it does — Supabase rejects any token that
  // isn't a real Cloudflare-issued one).
  useEffect(() => {
    if (!siteKey) {
      console.warn(
        '[TurnstileWidget] NEXT_PUBLIC_TURNSTILE_SITE_KEY not set — ' +
        'rendering without challenge. This MUST NOT happen in prod/staging.'
      );
      onToken('dev-no-captcha');
      onReady?.();
    }
    // siteKey is read once at module load; intentionally not in deps
    // so a hot-reload doesn't fire the warning twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!siteKey) {
    return null;
  }

  return (
    <div className={className}>
      <Turnstile
        ref={ref}
        siteKey={siteKey}
        options={{
          action,
          theme: 'auto',
          // 'managed' = Cloudflare picks invisible vs interactive
          // based on its own risk score. Most legitimate users see
          // nothing; only suspicious sessions get a checkbox.
          appearance: 'always',
          // 'normal' size — fits the auth-card width without
          // pushing the layout. 'flexible' would re-flow on
          // narrow screens but renders ugly on desktop.
          size: 'flexible',
          retry: 'auto',
          // Token TTL on Cloudflare's side is 300s. Refresh
          // automatically when it would otherwise expire mid-form.
          refreshExpired: 'auto',
        }}
        onSuccess={(token) => {
          onToken(token);
          onReady?.();
        }}
        onExpire={() => onToken(null)}
        onError={() => onToken(null)}
        onWidgetLoad={() => onReady?.()}
      />
    </div>
  );
}

/**
 * Convenience: in places where the parent needs to reset the widget
 * imperatively (e.g. after a 401 from the backend so the user can
 * retry without reloading), use this hook. Returns a ref to forward
 * to <TurnstileWidget>.
 *
 * Not used today — the auth pages reset by re-mounting the widget
 * via a `key` prop on form-submit failure, which is simpler. Kept
 * for future flows that need fine-grained control.
 */
export function useTurnstileReset() {
  const ref = useRef<TurnstileInstance | null>(null);
  return {
    ref,
    reset: () => ref.current?.reset(),
  };
}
